import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Star,
  BookOpen,
  MessageSquare,
  Compass,
  Rocket,
  TrendingUp,
  Heart,
  Check,
} from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import { Alert } from '@/client/components/ui/alert';
import { Seo } from '@/client/components/Seo';
import { api } from '@/client/lib/api';
import { isLocale, type Locale } from '@/client/i18n';

const AUDIENCE_CARDS = [
  {
    titleKey: 'landing.audience_card_1_title',
    bodyKey: 'landing.audience_card_1_body',
    Icon: Rocket,
    accent: 'from-primary/15 to-primary/0',
  },
  {
    titleKey: 'landing.audience_card_2_title',
    bodyKey: 'landing.audience_card_2_body',
    Icon: TrendingUp,
    accent: 'from-primary-light/30 to-primary/0',
  },
  {
    titleKey: 'landing.audience_card_3_title',
    bodyKey: 'landing.audience_card_3_body',
    Icon: Heart,
    accent: 'from-[#f7c5dc]/30 to-primary/0',
  },
];

export function Landing() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const prefix = `/${locale}`;

  return (
    <div>
      <Seo
        title={t('seo.landing_title')}
        description={t('seo.landing_description')}
        locale={locale}
      />

      {/* HERO */}
      <section className="relative">
        <div
          className="absolute top-10 -left-20 w-[28rem] h-[28rem] rounded-full pointer-events-none animate-float-orb"
          style={{
            background: 'radial-gradient(circle, rgba(193,167,226,0.45) 0%, transparent 65%)',
            filter: 'blur(36px)',
          }}
          aria-hidden
        />
        <div
          className="absolute top-40 right-0 w-[32rem] h-[32rem] rounded-full pointer-events-none animate-float-orb"
          style={{
            background: 'radial-gradient(circle, rgba(117,59,189,0.20) 0%, transparent 65%)',
            filter: 'blur(48px)',
            animationDelay: '-8s',
          }}
          aria-hidden
        />

        <div className="relative mx-auto max-w-6xl px-5 sm:px-6 lg:px-8 pt-16 sm:pt-24 lg:pt-32 pb-12 sm:pb-16">
          <div className="flex flex-col items-center text-center">
            <span className="inline-flex items-center gap-2 rounded-pill bg-white/70 backdrop-blur-md border border-glass-border-strong px-4 py-1.5 font-sans text-caption uppercase tracking-[0.18em] text-primary animate-fade-up shadow-glass-soft">
              <Star size={12} className="fill-primary" />
              {t('landing.eyebrow', { defaultValue: 'Dajana Rodriguez Academy' })}
            </span>

            <h1
              className="mt-6 font-display text-[clamp(2.5rem,7vw,4.5rem)] leading-[1.05] tracking-tight text-primary-dark max-w-4xl animate-fade-up"
              style={{ animationDelay: '70ms' }}
            >
              {t('landing.hero_title')}
            </h1>

            <p
              className="mt-6 font-display text-body-large sm:text-[1.25rem] leading-relaxed text-text-secondary max-w-2xl animate-fade-up"
              style={{ animationDelay: '140ms' }}
            >
              {t('landing.hero_subtitle')}
            </p>

            <div
              className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto animate-fade-up"
              style={{ animationDelay: '210ms' }}
            >
              <Link to={`${prefix}/kurzy`} className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto">
                  {t('landing.hero_cta_courses')}
                  <ArrowRight size={18} />
                </Button>
              </Link>
              <Link to={`${prefix}/konzultacie`} className="w-full sm:w-auto">
                <Button size="lg" variant="secondary" className="w-full sm:w-auto">
                  {t('landing.hero_cta_consultation')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* BLOK 2 — Offering trio: Kurzy / Konzultácia / Mentoring */}
      <section className="relative pt-4 pb-16 sm:pb-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
          <div className="grid gap-5 sm:gap-6 md:grid-cols-3 stagger-children">
            <OfferingCard
              to={`${prefix}/kurzy`}
              Icon={BookOpen}
              title={t('landing.offering_courses_title')}
              body={t('landing.offering_courses_body')}
            />
            <OfferingCard
              to={`${prefix}/konzultacie`}
              Icon={MessageSquare}
              title={t('landing.offering_consult_title')}
              body={t('landing.offering_consult_body')}
            />
            <OfferingCard
              to={`${prefix}/mentoring`}
              Icon={Compass}
              title={t('landing.offering_mentoring_title')}
              body={t('landing.offering_mentoring_body')}
            />
          </div>
        </div>
      </section>

      {/* BLOK 3 — O Academy */}
      <section className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-5xl px-5 sm:px-6 lg:px-8">
          <div className="dr-card-static p-8 sm:p-14 lg:p-20 text-center">
            <p className="dr-eyebrow">
              {t('landing.section_about_eyebrow', { defaultValue: 'Academy' })}
            </p>
            <h2 className="mt-4 font-display text-h2 sm:text-h1 text-primary-dark">
              {t('landing.section_about_title')}
            </h2>
            <p className="mt-6 font-display text-body-large text-text-secondary leading-relaxed max-w-2xl mx-auto whitespace-pre-line">
              {t('landing.section_about_body')}
            </p>
            <Link
              to={`${prefix}/o-academy`}
              className="inline-flex items-center gap-2 mt-8 font-sans text-body font-medium text-primary hover:text-primary-dark transition-colors group"
            >
              {t('landing.section_about_link')}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Full-width banner 1 */}
      <FullWidthBanner
        src="/photos/DR_Academy_homepage_hero.jpg"
        alt={
          locale === 'cs'
            ? 'Dajana Varga Rodriguez, zakladatelka Dajana Rodriguez Academy'
            : 'Dajana Varga Rodriguez, founderka Dajana Rodriguez Academy'
        }
      />

      {/* BLOK 4 — Pre koho je Academy */}
      <section className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="dr-eyebrow">
              {t('landing.section_audience_eyebrow', { defaultValue: 'Pre koho' })}
            </p>
            <h2 className="mt-4 font-display text-h2 sm:text-h1 text-primary-dark">
              {t('landing.section_audience_title')}
            </h2>
          </div>

          <div className="mt-12 sm:mt-16 grid gap-5 sm:gap-6 md:grid-cols-3 stagger-children">
            {AUDIENCE_CARDS.map(({ titleKey, bodyKey, Icon, accent }) => (
              <article key={titleKey} className="dr-card p-7 sm:p-8 group">
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}
                  aria-hidden
                />
                <div className="relative">
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 text-primary mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon size={24} />
                  </div>
                  <h3 className="font-display text-h3 text-primary-dark">{t(titleKey)}</h3>
                  <p className="mt-3 font-sans text-body text-text-secondary leading-relaxed whitespace-pre-line">
                    {t(bodyKey)}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Full-width banner 2 */}
      <FullWidthBanner
        src="/photos/DR_Academy_homepage_banner_2.jpg"
        alt={
          locale === 'cs'
            ? 'Dajana Varga Rodriguez, zakladatelka Dajana Rodriguez Academy'
            : 'Dajana Varga Rodriguez, founderka Dajana Rodriguez Academy'
        }
      />

      {/* BLOK 6 — Trojblok CTA */}
      <section className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
          <div className="grid gap-5 sm:gap-6 lg:grid-cols-3">
            <FeaturedCTA
              eyebrow={t('landing.featured_courses_eyebrow')}
              title={t('landing.featured_courses_title')}
              body={t('landing.featured_courses_body')}
              ctaLabel={t('landing.featured_courses_cta')}
              ctaTo={`${prefix}/kurzy`}
              variant="dark"
            />
            <FeaturedCTA
              eyebrow={t('landing.featured_consult_eyebrow')}
              title={t('landing.featured_consult_title')}
              body={t('landing.featured_consult_body')}
              ctaLabel={t('landing.featured_consult_cta')}
              ctaTo={`${prefix}/konzultacie`}
              variant="light"
            />
            <FeaturedCTA
              eyebrow={t('landing.featured_mentoring_eyebrow')}
              title={t('landing.featured_mentoring_title')}
              body={t('landing.featured_mentoring_body')}
              ctaLabel={t('landing.featured_mentoring_cta')}
              ctaTo={`${prefix}/mentoring`}
              variant="dark"
            />
          </div>
        </div>
      </section>

      <NewsletterSection locale={locale} />
    </div>
  );
}

function OfferingCard({
  to,
  Icon,
  title,
  body,
}: {
  to: string;
  Icon: typeof BookOpen;
  title: string;
  body: string;
}) {
  return (
    <Link to={to} className="dr-card p-7 sm:p-8 group flex flex-col h-full hover:-translate-y-1">
      <div className="relative">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 text-primary mb-5 group-hover:scale-110 transition-transform duration-300">
          <Icon size={20} />
        </div>
        <h3 className="font-sans text-caption uppercase tracking-[0.18em] text-primary font-semibold">
          {title}
        </h3>
        <p className="mt-2 font-display text-h4 text-primary-dark leading-tight">{body}</p>
        <span className="inline-flex items-center gap-1.5 mt-5 font-sans text-body-small font-medium text-primary group-hover:gap-2.5 transition-all">
          <ArrowRight size={14} />
        </span>
      </div>
    </Link>
  );
}

function FullWidthBanner({ src, alt }: { src: string; alt: string }) {
  return (
    <section aria-hidden={false} className="relative w-full bg-primary-light/30">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="block w-full h-[280px] sm:h-[400px] lg:h-[500px] object-cover"
      />
    </section>
  );
}

function FeaturedCTA({
  eyebrow,
  title,
  body,
  ctaLabel,
  ctaTo,
  variant = 'dark',
}: {
  eyebrow: string;
  title: string;
  body: string;
  ctaLabel: string;
  ctaTo: string;
  variant?: 'dark' | 'light';
}) {
  return (
    <article
      className={`relative overflow-hidden rounded-2xl p-8 sm:p-10 flex flex-col ${
        variant === 'dark'
          ? 'bg-gradient-to-br from-primary to-primary-dark text-white'
          : 'bg-white/80 backdrop-blur-xl border border-glass-border-strong shadow-glass'
      }`}
    >
      {variant === 'dark' && (
        <>
          <div
            className="absolute -top-20 -right-20 w-72 h-72 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0%, transparent 65%)',
            }}
            aria-hidden
          />
          <div
            className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(193,167,226,0.35) 0%, transparent 65%)',
              filter: 'blur(24px)',
            }}
            aria-hidden
          />
        </>
      )}
      <div className="relative flex flex-col h-full">
        <p
          className={`font-sans text-caption uppercase tracking-[0.18em] ${
            variant === 'dark' ? 'text-primary-light' : 'text-primary'
          }`}
        >
          {eyebrow}
        </p>
        <h3
          className={`mt-3 font-display text-h3 ${
            variant === 'dark' ? 'text-white' : 'text-primary-dark'
          }`}
        >
          {title}
        </h3>
        <p
          className={`mt-4 font-sans text-body leading-relaxed flex-1 ${
            variant === 'dark' ? 'text-white/85' : 'text-text-secondary'
          }`}
        >
          {body}
        </p>
        <Link to={ctaTo} className="inline-block mt-8">
          <Button variant={variant === 'dark' ? 'secondary' : 'primary'} size="md">
            {ctaLabel}
            <ArrowRight size={16} />
          </Button>
        </Link>
      </div>
    </article>
  );
}

function NewsletterSection({ locale }: { locale: Locale }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setStatus('submitting');
    try {
      await api('/api/v1/newsletter/subscribe', {
        method: 'POST',
        body: { email, locale },
      });
      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
    }
  }

  const benefitKeys = [
    'landing.newsletter_benefit_1',
    'landing.newsletter_benefit_2',
    'landing.newsletter_benefit_3',
  ];

  return (
    <section className="relative py-20 sm:py-28">
      <div
        className="absolute inset-x-0 top-0 bottom-0 -z-10"
        style={{
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(193,167,226,0.20), transparent 60%),' +
            'linear-gradient(180deg, rgba(193,167,226,0.10) 0%, rgba(193,167,226,0.05) 100%)',
        }}
        aria-hidden
      />
      <div className="mx-auto max-w-2xl px-5 sm:px-6 lg:px-8 text-center">
        <p className="dr-eyebrow">{t('landing.newsletter_eyebrow', { defaultValue: 'Newsletter' })}</p>
        <h2 className="mt-4 font-display text-h2 sm:text-h1 text-primary-dark">
          {t('landing.newsletter_title')}
        </h2>
        <p className="mt-5 font-display text-body-large text-text-secondary leading-relaxed">
          {t('landing.newsletter_subtitle')}
        </p>

        <ul className="mt-8 space-y-3 text-left max-w-lg mx-auto">
          {benefitKeys.map((key) => (
            <li key={key} className="flex items-start gap-3">
              <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-white">
                <Check size={12} strokeWidth={3} />
              </span>
              <span className="font-sans text-body text-text-primary leading-relaxed">
                {t(key)}
              </span>
            </li>
          ))}
        </ul>

        {status === 'success' ? (
          <Alert variant="success" className="mt-8 max-w-md mx-auto text-center">
            {t('landing.newsletter_success')}
          </Alert>
        ) : (
          <form
            onSubmit={onSubmit}
            className="mt-10 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('landing.newsletter_email_placeholder')}
              className="flex-1 h-[3.25rem] px-5"
              aria-label={t('auth.email_label')}
            />
            <Button type="submit" size="lg" disabled={status === 'submitting'}>
              {status === 'submitting' ? t('common.loading') : t('landing.newsletter_submit')}
            </Button>
          </form>
        )}
        {status === 'error' && (
          <Alert variant="error" className="mt-4 max-w-md mx-auto">
            {t('landing.newsletter_error')}
          </Alert>
        )}
      </div>
    </section>
  );
}
